"use client";

import { updateLocale } from "@i18n/lib/update-locale";
import { useLocaleRouter } from "@i18n/routing";
import { config } from "@repo/config";
import type { Locale } from "@repo/i18n";
import { Button } from "@ui/components/button";
import { X } from "lucide-react";
import { useLocale } from "next-intl";
import { useEffect, useState } from "react";

const { locales } = config.i18n;

interface GeoDetectionResult {
	locale: string;
	country: string;
	countryCode?: string;
	source: string;
}

export function LanguageBannerGeo() {
	const currentLocale = useLocale();
	const router = useLocaleRouter();
	const [showBanner, setShowBanner] = useState(false);
	const [suggestedLocale, setSuggestedLocale] = useState<string | null>(null);
	const [detectionSource, setDetectionSource] = useState<string>("");

	useEffect(() => {
		// 检查是否已经显示过横幅
		const bannerDismissed = localStorage.getItem(
			"language-banner-dismissed",
		);
		if (bannerDismissed) return;

		async function detectPreferredLocale() {
			let detected = false;

			// 1. 首先检查浏览器语言偏好
			const browserLanguages = navigator.languages || [
				navigator.language,
			];

			const hasJapanesePreference = browserLanguages.some(
				(lang) =>
					lang.toLowerCase().includes("ja") ||
					lang.toLowerCase().includes("jp"),
			);

			const hasTraditionalChinesePreference = browserLanguages.some(
				(lang) =>
					lang.toLowerCase().includes("zh-tw") ||
					lang.toLowerCase().includes("zh-hk") ||
					lang.toLowerCase().includes("zh-mo"),
			);

			const hasSimplifiedChinesePreference = browserLanguages.some(
				(lang) =>
					lang.toLowerCase().includes("zh-cn") ||
					lang.toLowerCase().includes("zh-sg"),
			);

			const hasChinesePreference = browserLanguages.some(
				(lang) =>
					lang.toLowerCase().includes("zh") ||
					lang.toLowerCase().includes("cn"),
			);

			// 优先级：日语 > 繁体中文 > 简体中文
			if (hasJapanesePreference && currentLocale !== "ja") {
				setSuggestedLocale("ja");
				setDetectionSource("ブラウザ言語設定");
				setShowBanner(true);
				detected = true;
			} else if (
				hasTraditionalChinesePreference &&
				currentLocale !== "zh-TW"
			) {
				setSuggestedLocale("zh-TW");
				setDetectionSource("瀏覽器語言偏好");
				setShowBanner(true);
				detected = true;
			} else if (
				hasSimplifiedChinesePreference &&
				currentLocale !== "zh"
			) {
				setSuggestedLocale("zh");
				setDetectionSource("浏览器语言偏好");
				setShowBanner(true);
				detected = true;
			} else if (
				hasChinesePreference &&
				currentLocale !== "zh" &&
				currentLocale !== "zh-TW"
			) {
				setSuggestedLocale("zh");
				setDetectionSource("浏览器语言偏好");
				setShowBanner(true);
				detected = true;
			}

			// 2. 如果浏览器检测没有结果，则使用地理位置检测
			if (!detected) {
				try {
					const response = await fetch("/api/detect-locale");
					const geoResult: GeoDetectionResult = await response.json();

					if (geoResult.locale === "ja" && currentLocale !== "ja") {
						setSuggestedLocale("ja");
						setDetectionSource(`地理的位置 (${geoResult.country})`);
						setShowBanner(true);
					} else if (
						geoResult.locale === "zh-TW" &&
						currentLocale !== "zh-TW"
					) {
						setSuggestedLocale("zh-TW");
						setDetectionSource(`地理位置 (${geoResult.country})`);
						setShowBanner(true);
					} else if (
						geoResult.locale === "zh" &&
						currentLocale !== "zh"
					) {
						setSuggestedLocale("zh");
						setDetectionSource(`地理位置 (${geoResult.country})`);
						setShowBanner(true);
					} else if (
						geoResult.locale === "en" &&
						(currentLocale === "zh" ||
							currentLocale === "zh-TW" ||
							currentLocale === "ja")
					) {
						setSuggestedLocale("en");
						setDetectionSource(`地理位置 (${geoResult.country})`);
						setShowBanner(true);
					}
				} catch (error) {
					console.warn("Geo-detection failed:", error);
				}
			}
		}

		detectPreferredLocale();
	}, [currentLocale]);

	// 动态设置 CSS 变量，用于调整 NavBar 位置
	useEffect(() => {
		if (showBanner) {
			document.documentElement.style.setProperty(
				"--language-banner-height",
				"60px",
			);
		} else {
			document.documentElement.style.setProperty(
				"--language-banner-height",
				"0px",
			);
		}

		// 清理函数
		return () => {
			document.documentElement.style.setProperty(
				"--language-banner-height",
				"0px",
			);
		};
	}, [showBanner]);

	const handleSwitchLanguage = async () => {
		if (!suggestedLocale) return;

		// 记住用户选择
		localStorage.setItem("language-banner-dismissed", "true");

		// 更新locale cookie
		await updateLocale(suggestedLocale as Locale);

		// 跳转到对应语言版本
		const currentPath = window.location.pathname;
		const pathWithoutLocale = currentPath.replace(
			/^\/[a-z]{2}(-[A-Z]{2})?(\/|$)/,
			"/",
		);
		const newPath =
			suggestedLocale === config.i18n.defaultLocale
				? pathWithoutLocale
				: `/${suggestedLocale}${pathWithoutLocale}`;

		window.location.href = newPath;
	};

	const handleDismiss = () => {
		localStorage.setItem("language-banner-dismissed", "true");
		setShowBanner(false);
	};

	if (!showBanner || !suggestedLocale) return null;

	const getLocaleName = (locale: string) => {
		return locales[locale as keyof typeof locales]?.label || locale;
	};

	const getMessage = () => {
		if (suggestedLocale === "ja") {
			return {
				text: `${detectionSource}により、より良いエクスペリエンスのために日本語版をおすすめします`,
				switch: "日本語に切り替える",
				dismiss: "現在の言語を続ける",
			};
		}

		if (suggestedLocale === "zh") {
			return {
				text: `基于${detectionSource}检测，建议您使用简体中文版本以获得更好的体验`,
				switch: "切换到简体中文",
				dismiss: "继续使用当前语言",
			};
		}

		if (suggestedLocale === "zh-TW") {
			return {
				text: `基於${detectionSource}偵測，建議您使用繁體中文版本以獲得更好的體驗`,
				switch: "切換到繁體中文",
				dismiss: "繼續使用當前語言",
			};
		}

		return {
			text: `Based on ${detectionSource}, we suggest using ${getLocaleName(suggestedLocale)} for a better experience`,
			switch: `Switch to ${getLocaleName(suggestedLocale)}`,
			dismiss: "Continue in current language",
		};
	};

	const message = getMessage();

	return (
		<div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200 px-4 py-3">
			<div className="container max-w-6xl mx-auto">
				<div className="flex items-center justify-center relative">
					{/* 主要内容居中显示 */}
					<div className="flex items-center space-x-4">
						<div className="flex items-center space-x-2">
							<div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
							<p className="text-sm text-blue-800 font-medium">
								{message.text}
							</p>
						</div>
						<div className="flex space-x-2">
							<Button
								onClick={handleSwitchLanguage}
								size="sm"
								className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1"
							>
								{message.switch}
							</Button>
							<Button
								onClick={handleDismiss}
								size="sm"
								variant="outline"
								className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs px-3 py-1"
							>
								{message.dismiss}
							</Button>
						</div>
					</div>

					{/* 关闭按钮固定在右侧 */}
					<Button
						onClick={handleDismiss}
						size="sm"
						variant="ghost"
						className="absolute right-0 text-blue-600 hover:bg-blue-100 p-2 min-w-[40px] min-h-[40px] flex items-center justify-center"
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
			</div>
		</div>
	);
}
